import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styles from '../../assets/styles/MimaDropzone.module.scss';
import { MdCancel } from 'react-icons/md';
import PropTypes from 'prop-types';
import MimaText from '../MimaText';
import fileIcon from '../../assets/img/file-icon.png';

const propTypes = {
	labelTitle: PropTypes.string,
	title: PropTypes.string,
	info: PropTypes.string,
	info2: PropTypes.string,
	maxNumOfImages: PropTypes.number,
	files: PropTypes.array,
	name: PropTypes.string,
	setFiles: PropTypes.any,
	errorMsg: PropTypes.string,
	fileTypes: PropTypes.array,
};

const MimaDropZone = ({
	title = 'Upload file',
	info,
	info2,
	maxNumOfImages,
	files = [],
	setFiles,
	labelTitle,
	name,
	errorMsg,
	fileTypes = [],
}) => {
	const [rejected, setRejected] = useState([]);

	const onDrop = useCallback(
		(acceptedFiles, rejectedFiles) => {
			const filesWithPreview = acceptedFiles.map(file =>
				Object.assign(file, { preview: URL.createObjectURL(file) })
			);

			setFiles([...files, ...filesWithPreview]);

			if (rejectedFiles?.length) {
				setRejected(previousFiles => [...previousFiles, ...rejectedFiles]);
			}
		},
		[files, setFiles]
	);

	const FILE_TYPE_MAP = {
		image: ['image/*'],
		video: ['video/*'],
		doc: [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'text/plain',
		],
	};

	const getAcceptedTypes = fileTypes => {
		return fileTypes.reduce((acc, type) => {
			const mimes = FILE_TYPE_MAP[type];
			if (mimes) {
				mimes.forEach(mime => (acc[mime] = []));
			}
			return acc;
		}, {});
	};

	const acceptedTypes =
		fileTypes?.length > 0 ? getAcceptedTypes(fileTypes) : [];

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: {
			'image/*': [],
			...acceptedTypes,
		},
		maxSize: 1024 * 1000,
		maxFiles: maxNumOfImages,
		onDrop,
	});

	const getFileSrc = file => {
		if (typeof file === 'string') {
			return file;
		}

		return file?.preview || file?.url || file?.src || file?.path || '';
	};

	const getFileName = file => {
		if (typeof file === 'string') {
			const nameFromUrl = file.split('/').pop() || 'file';
			return decodeURIComponent(nameFromUrl);
		}

		if (file?.name) {
			return file.name;
		}

		const src = getFileSrc(file);
		const nameFromUrl = src?.split('/').pop() || 'file';
		return decodeURIComponent(nameFromUrl);
	};

	const isImageFile = file => {
		if (file?.type?.startsWith?.('image/')) {
			return true;
		}

		const src = getFileSrc(file);
		if (!src) {
			return false;
		}

		const cleanSrc = src.split('?')[0].split('#')[0];
		return /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i.test(cleanSrc);
	};

	const getFileId = file => {
		if (typeof file === 'string') {
			return file;
		}

		return file?.name || getFileSrc(file);
	};

	const removeFile = fileToRemove => {
		const removeId = getFileId(fileToRemove);
		setFiles(files.filter(file => getFileId(file) !== removeId));
	};

	useEffect(() => {
		const removeRejected = () => {
			if (rejected.length > 0) {
				setTimeout(() => {
					setRejected([]);
				}, 5000);
			}
		};

		removeRejected();
	}, [rejected]);

	return (
		<div className={styles.group}>
			<label htmlFor={name}>{labelTitle}</label>
			<div className={styles.outline}>
				{files?.length < maxNumOfImages ? (
					<div {...getRootProps()}>
						<input {...getInputProps()} />
						{isDragActive ? (
							<p>Drop the files here ...</p>
						) : (
							<div className={styles.content}>
								<div className={styles.upload}>{title}</div>
								<p>{info}</p>
								<p>{info2}</p>
							</div>
						)}
					</div>
				) : (
					''
				)}
				<div className={styles.preview}>
					{Array.isArray(files) &&
						files?.map((file, index) => (
							<div key={`${getFileId(file)}-${index}`}>
								<img
									src={isImageFile(file) ? getFileSrc(file) : fileIcon}
									alt={`Preview ${getFileName(file)}`}
									// onLoad={() => {
									//   file?.preview ? URL.revokeObjectURL(file?.preview) : '';
									// }}
									// width={100}
									// height={100}
								/>
								<p>{getFileName(file)}</p>
								<button
									onClick={() => {
										removeFile(file);
									}}
									className={styles.remove}>
									<MdCancel />
								</button>
							</div>
						))}
				</div>

				<div>
					{rejected.map(({ file, errors }) => (
						<div key={file?.name} className={styles.rejected}>
							<p>{file?.name} cannot be uploaded</p>
							{errors.map(error => (
								<p key={error.code} className={styles.rejected__msg}>
									{error.message}
								</p>
							))}
						</div>
					))}
				</div>
			</div>
			{errorMsg ? (
				<MimaText variant="small" color="var(--color-error)" mt={1}>
					{errorMsg}
				</MimaText>
			) : (
				''
			)}
		</div>
	);
};

MimaDropZone.propTypes = propTypes;

export default MimaDropZone;
